import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { DynamicPage, DynamicPageTag } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import { Popover, PopoverTrigger } from '@superfunnel/ui-core/v2/popover';
import {
  PopoverPanelBody,
  PopoverPanelContent,
  PopoverPanelHeader,
  PopoverPanelTitle,
} from '@superfunnel/ui-core/v2/PopoverPanel';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';

interface DynamicPageFilters {
  tags: DynamicPageTag[];
  statuses: (DynamicPage['status'] | 'inactive')[];
  changes: boolean[];
  domains: string[];
}

type PartialDynamicPageFilters = {
  tags?: string[];
  statuses?: DynamicPageFilters['statuses'];
  changes?: boolean[];
  domains?: string[];
};

interface Props {
  children: ReactNode;
  disabled?: boolean;
  filters: PartialDynamicPageFilters;
  filterOptions: DynamicPageFilters;
  onFiltersChange: (filters: PartialDynamicPageFilters) => void;
  onReset: () => void;
}

export default function DynamicPagesFilterPopover({
  children,
  disabled,
  filters,
  filterOptions,
  onFiltersChange,
  onReset,
}: Props) {
  const [open, setOpen] = useState(false);
  const [_freeTextTag, _setFreeTextTag] = useState<string>('');

  // Initialize selectedDynamicPageTags from current filters
  const selectedDynamicPageTags = useMemo(() => {
    if (!filters.tags || !filterOptions.tags) return [];
    return filterOptions.tags.filter((tag) => filters.tags?.includes(tag.id));
  }, [filters.tags, filterOptions.tags]);

  const handleTagsChange = (newTags: DynamicPageTag[]) => {
    onFiltersChange({
      ...filters,
      tags: newTags.map((tag) => tag.id),
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.changes && filters.changes.length > 0) ||
      (filters.tags && filters.tags.length > 0) ||
      (filters.domains && filters.domains.length > 0)
    );
  }, [filters]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverPanelContent width="md" align="end">
        <PopoverPanelHeader>
          <div className="flex w-full items-center justify-between gap-2">
            <PopoverPanelTitle>Filters</PopoverPanelTitle>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="micro"
                onClick={onReset}
                className="text-sm"
              >
                Reset all
              </Button>
            )}
          </div>
        </PopoverPanelHeader>
        <PopoverPanelBody className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <h4 className="text-base-700 text-sm font-medium">Status</h4>
            <Tabs
              value={
                filters.statuses?.length === 0 || !filters.statuses
                  ? 'all'
                  : filters.statuses?.length === filterOptions.statuses.length
                    ? 'all'
                    : filters.statuses?.[0] || ''
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  onFiltersChange({
                    ...filters,
                    statuses: [],
                  });
                } else {
                  onFiltersChange({
                    ...filters,
                    statuses: [value] as DynamicPageFilters['statuses'],
                  });
                }
              }}
            >
              <TabsList className="w-full">
                <TabsTrigger value="all" className="flex-1">
                  All
                </TabsTrigger>
                {filterOptions.statuses.map((status) => (
                  <TabsTrigger key={status} value={status} className="flex-1">
                    {status === 'active' ? 'Active' : 'Inactive'}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Changes */}
          <div className="space-y-1.5">
            <h4 className="text-base-700 text-sm font-medium">Changes</h4>
            <Tabs
              value={
                filters.changes?.length === 0 || !filters.changes
                  ? 'all'
                  : filters.changes?.length === filterOptions.changes.length
                    ? 'all'
                    : String(filters.changes?.[0]) || ''
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  onFiltersChange({
                    ...filters,
                    changes: [],
                  });
                } else {
                  onFiltersChange({
                    ...filters,
                    changes: [value === 'true'],
                  });
                }
              }}
            >
              <TabsList className="w-full">
                <TabsTrigger value="all" className="flex-1">
                  All
                </TabsTrigger>
                {filterOptions.changes.map((change) => (
                  <TabsTrigger
                    key={String(change)}
                    value={String(change)}
                    className="flex-1"
                  >
                    {change ? 'Pending' : 'Deployed'}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <ChipsAutocomplete<DynamicPageTag>
              title="Tags"
              placeholder="Search tags..."
              options={filterOptions.tags || []}
              getOptionId={(c) => c.id}
              getValue={(c) => c.name}
              selected={selectedDynamicPageTags}
              onChange={handleTagsChange}
              className="w-full"
            />
          </div>

          {/* Domains */}
          <div className="space-y-1.5">
            <ChipsAutocomplete<string>
              title="Domains"
              placeholder="Search domains..."
              options={filterOptions.domains || []}
              getOptionId={(d) => d}
              getValue={(d) => d}
              selected={filters.domains || []}
              onChange={(newDomains) => {
                onFiltersChange({
                  ...filters,
                  domains: newDomains,
                });
              }}
              className="w-full"
            />
          </div>
        </PopoverPanelBody>
      </PopoverPanelContent>
    </Popover>
  );
}
